import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import { createTheme, ThemeProvider, withStyles } from "@material-ui/core/styles";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import { Rating } from "@material-ui/lab";
import { Evaluation_Format, EvaluationStatus } from "@helpers/_constants";

export default function NotationPreview(props) {
  const {note, evaluationFormat} = props;

  if (!note) {
    return null;
  }

  if (
    evaluationFormat === Evaluation_Format.SKILL_LEVEL_10 ||
    evaluationFormat === Evaluation_Format.SKILL_LEVEL_5
  ) {
    return <SkillLevelNotation note={note} />;
  }

  if (
    evaluationFormat === Evaluation_Format.NOTE_10 ||
    evaluationFormat === Evaluation_Format.NOTE_5 ||
    evaluationFormat === Evaluation_Format.NOTE_4
  ) {
    return <NoteNotation note={note} evaluationFormat={evaluationFormat} />;
  }

  if (evaluationFormat === Evaluation_Format.NOTE_3_STARS) {
    return <StarNotation note={note} />;
  }

  return <PercentNotation note={note} />;
}

const Evaluation_noteStatus = {
  BEGINER: 0,
  JUNIOR: 20,
  INTERMEDIARY: 50,
  EXPERIENCED: 80
};

const statusBgColor = {
  [EvaluationStatus.BEGINER]: "arh-bg-[#f6f6f6]",
  [EvaluationStatus.JUNIOR]: "arh-bg-[#F8F1B6]",
  [EvaluationStatus.INTERMEDIARY]: "arh-bg-[#D3F0C9]",
  [EvaluationStatus.EXPERIENCED]: "arh-bg-[#A7EBDE]"
};

function SkillLevelNotation(props) {
  const {note} = props;
  const {t} = useTranslation("assesment.frequency");

  const status = useMemo(() => {
    if (note >= Evaluation_noteStatus.EXPERIENCED) {
      return EvaluationStatus.EXPERIENCED;
    }

    if (note >= Evaluation_noteStatus.INTERMEDIARY) {
      return EvaluationStatus.INTERMEDIARY;
    }

    if (note >= Evaluation_noteStatus.JUNIOR) {
      return EvaluationStatus.JUNIOR;
    }

    return EvaluationStatus.BEGINER;
  }, [note]);

  return (
    <div className="arh-flex arh-items-center arh-justify-center">
      <div
        className={`arh-w-[8px] arh-h-[8px] arh-rounded-full arh-mr-[0.5rem] ${statusBgColor[status]} `}
      ></div>
      <span className="arh-font-poppins arh-font-medium arh-text-black arh-text-xs ">
        {t(status)}
      </span>
    </div>
  );
}

const MAX_BY_FORMAT = {
  [Evaluation_Format.NOTE_5]: 5,
  [Evaluation_Format.NOTE_4]: 4,
  [Evaluation_Format.NOTE_10]: 10
};

function NoteNotation(props) {
  const {note: noteInPercent, evaluationFormat} = props;
  const maxValue = MAX_BY_FORMAT[evaluationFormat];

  const actualNote = useMemo(() => {
    const scale = +(100 / maxValue).toFixed(2);

    return +(noteInPercent / scale).toFixed(2);
  }, [noteInPercent, evaluationFormat]);

  return (
    <div className="arh-flex arh-items-center arh-justify-center">
      <span className="arh-font-poppins arh-font-medium arh-text-black arh-text-xs ">
        {actualNote}/{maxValue}
      </span>
    </div>
  );
}

const StyledRating = withStyles({
  iconFilled: {
    color: "#81dac9"
  },
  iconHover: {
    color: "#73C7B7"
  }
})(Rating);

const arhTheme = createTheme({
  palette: {
    secondary: {
      main: "#73C7B7"
    },
    primary: {
      main: "#73C7B7"
    }
  },
  overrides: {
    MuiBox: {
      root: {
        marginBottom: "0"
      }
    }
  }
});

function StarNotation(props) {
  const {note: noteInPercent} = props;

  const actualNote = useMemo(() => {
    const scale = +(100 / 3).toFixed(2);

    return Math.round(noteInPercent / scale);
  }, [noteInPercent]);

  return (
    <div className="arh-flex arh-items-center arh-justify-center">
      <ThemeProvider theme={arhTheme}>
        <Box component="fieldset" mb={2} borderColor="transparent">
          <StyledRating
            value={actualNote}
            max={3}
            precision={1}
            emptyIcon={<StarBorderIcon fontSize="inherit" />}
            readOnly
          />
        </Box>
      </ThemeProvider>
    </div>
  );
}

function PercentNotation(props) {
  let {note} = props;
  note = parseFloat(parseFloat(note).toFixed(2));
  return (
    <div className="arh-flex arh-items-center arh-justify-center">
      <span className="arh-font-poppins arh-font-medium arh-text-black arh-text-xs ">
        {note}%
      </span>
    </div>
  );
}
